import clsx from 'clsx'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

export interface InlineTextButtonProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode
  disabled?: boolean
}

/**
 * A button that flows inline with text and looks exactly like the Link component.
 * Use it when something should look like a link but performs an in-app action
 * instead of navigating to a URL — e.g. an inline citation opening the source
 * viewer. For navigation, use Link.
 *
 * It sets no font size or tracking of its own: it is part of a sentence, so the
 * surrounding text decides. The classes intentionally duplicate Link's; the
 * parity test in InlineTextButton.test.tsx fails if the two ever drift.
 */
export const InlineTextButton = forwardRef<HTMLSpanElement, InlineTextButtonProps>(function InlineTextButton(
  { children, className, disabled, onClick, onKeyDown, ...props },
  ref
) {
  return (
    // span[role=button], not a native <button>: a button is an atomic inline-level box
    // (browsers force inline-block), so mid-sentence it can never wrap across line
    // boxes with its text — it breaks the sentence around it. A span fragments like
    // any other inline text; Enter/Space activation keeps button keyboard semantics.
    <span
      ref={ref}
      {...props}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      data-ds-component="InlineTextButton"
      onClick={(event) => {
        if (disabled) {
          event.preventDefault()
          return
        }
        onClick?.(event)
      }}
      onKeyDown={(event) => {
        if (disabled) return
        onKeyDown?.(event)
        if (event.defaultPrevented) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          event.currentTarget.click()
        }
      }}
      className={clsx(
        'inline font-medium underline',
        'text-text-link transition-colors duration-150',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:text-link-text-hover',
        'focus:outline-hidden focus-visible:outline focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-link-focus-outline',
        className
      )}
    >
      {children}
    </span>
  )
})
